import type { $Fetch } from 'ofetch';
import type {
  HorasConfigGateway,
  HorasLancamentoGateway,
  ListHorasFilaParams,
} from '@/application/port/out/HorasComplementaresGateway';
import type {
  AprovarHorasCommand,
  HorasCategoria,
  HorasCategoriaCommand,
  HorasConfig,
  HorasConfigCommand,
  HorasLancamento,
  RejeitarHorasCommand,
} from '@/domain/model/HorasComplementares';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const CONFIGS_ROOT = '/api/v1/acadadmin/horas-complementares/configs';
const LANCAMENTOS_ROOT = '/api/v1/acadadmin/horas-complementares/lancamentos';

/**
 * HTTP gateway da configuração de horas complementares por curso.
 * Authority: `acadadmin:horas:read|manage`.
 *
 * @since paridade SimpleAcademy — Horas Complementares (2026-07-15)
 */
export class HttpHorasConfigGateway implements HorasConfigGateway {
  constructor(private readonly client: $Fetch) {}

  async listConfigs(): Promise<HorasConfig[]> {
    const res = await this.client<ApiResponse<HorasConfig[]>>(CONFIGS_ROOT, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async createConfig(cmd: HorasConfigCommand): Promise<HorasConfig> {
    const res = await this.client<ApiResponse<HorasConfig>>(
      CONFIGS_ROOT, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async updateConfig(id: string, cmd: HorasConfigCommand): Promise<HorasConfig> {
    const res = await this.client<ApiResponse<HorasConfig>>(
      `${CONFIGS_ROOT}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivateConfig(id: string): Promise<void> {
    // DELETE 204 No Content — sem envelope, não chamar assertApiSuccess.
    await this.client(`${CONFIGS_ROOT}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listCategorias(configId: string): Promise<HorasCategoria[]> {
    const res = await this.client<ApiResponse<HorasCategoria[]>>(
      `${CONFIGS_ROOT}/${encodeURIComponent(configId)}/categorias`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async createCategoria(configId: string, cmd: HorasCategoriaCommand): Promise<HorasCategoria> {
    const res = await this.client<ApiResponse<HorasCategoria>>(
      `${CONFIGS_ROOT}/${encodeURIComponent(configId)}/categorias`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async updateCategoria(categoriaId: string, cmd: HorasCategoriaCommand): Promise<HorasCategoria> {
    const res = await this.client<ApiResponse<HorasCategoria>>(
      `${CONFIGS_ROOT}/categorias/${encodeURIComponent(categoriaId)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivateCategoria(categoriaId: string): Promise<void> {
    await this.client(
      `${CONFIGS_ROOT}/categorias/${encodeURIComponent(categoriaId)}`, { method: 'DELETE' });
  }
}

/**
 * HTTP gateway da fila e do parecer sobre lançamentos.
 *
 * @since paridade SimpleAcademy — Horas Complementares (2026-07-15)
 */
export class HttpHorasLancamentoGateway implements HorasLancamentoGateway {
  constructor(private readonly client: $Fetch) {}

  async fila(params: ListHorasFilaParams): Promise<Page<HorasLancamento>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 20,
    };
    if (params.status) query.status = params.status;
    const res = await this.client<ApiResponse<Page<HorasLancamento>>>(
      LANCAMENTOS_ROOT, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<HorasLancamento> {
    const res = await this.client<ApiResponse<HorasLancamento>>(
      `${LANCAMENTOS_ROOT}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async aprovar(id: string, cmd: AprovarHorasCommand): Promise<HorasLancamento> {
    const res = await this.client<ApiResponse<HorasLancamento>>(
      `${LANCAMENTOS_ROOT}/${encodeURIComponent(id)}/aprovar`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async rejeitar(id: string, cmd: RejeitarHorasCommand): Promise<HorasLancamento> {
    const res = await this.client<ApiResponse<HorasLancamento>>(
      `${LANCAMENTOS_ROOT}/${encodeURIComponent(id)}/rejeitar`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async baixarComprovante(id: string): Promise<Blob> {
    // Proxy do backend: chega Blob cru (sem envelope ApiResponse).
    return this.client(`${LANCAMENTOS_ROOT}/${encodeURIComponent(id)}/comprovante`, {
      method: 'GET',
      responseType: 'blob',
    });
  }
}
